import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { mergeMap } from 'rxjs';
import { UsersService } from '@mzima-client/sdk';
import { ResourceService } from './resource.service';
import { EnvService } from './env.service';
import { SessionService } from './session.service';
import { GtmTrackingService } from './gtm-tracking.service';
import { EnumGtmEvent } from '@enums';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ResourceService<any> {
  constructor(
    protected override httpClient: HttpClient,
    protected override env: EnvService,
    private sessionService: SessionService,
    private userService: UsersService,
    private translate: TranslateService,
    private gtm: GtmTrackingService,
    private router: Router,
  ) {
    super(httpClient, env);
  }

  getApiVersions(): string {
    return '';
  }

  getResourceUrl(): string {
    const authType = this.env.environment.auth_type;
    return authType === 'password' ? 'oauth/token' : 'oauth/passwordless';
  }

  // login(username: string, password: string): Observable<any> {
  //   const payload = {
  //     username: username,
  //     password: password,
  //     grant_type: 'password',
  //     client_id: this.env.environment.oauth_client_id,
  //     client_secret: this.env.environment.oauth_client_secret,
  //     scope: generalHelpers.CONST.CLAIMED_USER_SCOPES.join(' '),
  //   };
  //   return super.post(payload).pipe(
  //     mergeMap(async (authResponse) => {
  //       this.loginUser(authResponse);
  //     }),
  //   );
  // }

  private setCurrentUserToSession(user: any) {
    this.sessionService.setCurrentUser({
      userId: user.id,
      realname: user.realname,
      email: user.email,
      role: user.role,
      permissions: user.permissions,
      allowed_privileges: user.allowed_privileges,
      gravatar: user.gravatar,
      language: user.language,
      phone_number: user.phone_number,
      country_code: user.country_code,
    });
  }

  loginUser(authResponse: any) {
    const accessToken = authResponse.access_token;

    if (authResponse.expires_in) {
      this.sessionService.setSessionData({
        accessToken,
        accessTokenExpires: Math.floor(Date.now() / 1000) + authResponse.expires_in,
        grantType: 'client_credentials',
        tokenType: authResponse.token_type,
      });
    } else if (authResponse.expires) {
      this.sessionService.setSessionData({
        accessToken,
        accessTokenExpires: authResponse.expires,
        grantType: 'client_credentials',
        tokenType: authResponse.token_type,
      });
    }
    return this.userService.getCurrentUser().subscribe({
      next: (userData: any) => {
        const { result } = userData;
        this.gtm.setUserLayer(result);
        this.gtm.registerEvent({ event: EnumGtmEvent.Login });
        this.setCurrentUserToSession(result);
        this.userService.dispatchUserEvents({ result });
      },
    });
  }

  signup(email: string, password: string, realname: string, phone: string) {
    const authType = this.env.environment.auth_type;
    const countryCode = authType === 'phone' ? '1' : '';
    const areaCode = authType === 'phone' ? phone.substring(0, 3) : '';
    const payload = {
      email,
      password,
      realname,
      phone_number: phone,
      country_code: countryCode,
      area_code: areaCode,
    };

    return this.httpClient.post(
      `${this.env.environment.backend_url}${this.env.environment.api_v5}register`,
      payload,
    );
  }

  verifyOtpAndSignup(
    email: string,
    password: string,
    realname: string,
    phone: string,
    countryCode: string,
    otp: string,
  ) {
    const authType = this.env.environment.auth_type;
    const areaCode = authType === 'phone' ? phone.substring(0, 3) : '';
    const payload = {
      email,
      password,
      realname,
      phone_number: `+${countryCode}${phone}`,
      country_code: countryCode,
      area_code: areaCode,
      otp,
    };

    return this.httpClient
      .post(`${this.env.environment.backend_url}${this.env.environment.api_v5}register`, payload)
      .pipe(
        mergeMap(async (authResponse: any) => {
          return this.loginUser(authResponse);
        }),
      );
  }

  requestOtp(phone: string, countryCode: string, type: string) {
    const payload = {
      phoneNumber: `+${countryCode}${phone}`,
      type,
    };

    return this.httpClient.post(
      `${this.env.environment.backend_url}${this.env.environment.api_v5}otp/request`,
      payload,
    );
  }

  verifyLoginOtp(phone: string, countryCode: string, otp: string) {
    const payload = {
      phoneNumber: `+${countryCode}${phone}`,
      otp,
    };

    return this.httpClient
      .post(`${this.env.environment.backend_url}oauth/phone/verify`, payload)
      .pipe(
        mergeMap(async (authResponse: any) => {
          return this.loginUser(authResponse);
        }),
      );
  }

  resetPassword(payload: { email: string }) {
    return this.httpClient.post(
      `${this.env.environment.backend_url}${this.env.environment.api_v5}passwordreset`,
      payload,
    );
  }

  restorePassword(payload: { token: string; password: string }) {
    return this.httpClient.post(
      `${this.env.environment.backend_url}${this.env.environment.api_v5}passwordreset/confirm`,
      payload,
    );
  }
  public logout() {
    this.httpClient
      .post(`${this.env.environment.backend_url}${this.env.environment.api_v5}logout`, {})
      .subscribe({
        next: () => {
          this.clearData();
        },
      });
  }

  public clearData() {
    this.gtm.clearUserLayer();
    this.sessionService.clearSessionData();
    this.sessionService.clearUserData();

    if (this.sessionService.accessToSite) {
      this.router.navigate(['/map']);
    } else {
      this.router.navigate(['/forbidden']);
    }
  }

  public getControlError(form: FormGroup, field: string, errorCodes: string[]) {
    for (const errorCode of errorCodes) {
      if (form.controls[field].hasError(errorCode)) {
        return this.translate.instant(`user.valid.${field}.${errorCode}`);
      }
    }
  }
}
